/**
 * Translation key constants.
 * Usage: t(KEYS.NAV.HOME), t(KEYS.COMMON.READ_MORE), etc.
 * Keys map to nested objects in locale files resolved by i18next dot-notation.
 */
export const KEYS = {
  // ─── Navigation ────────────────────────────────────────────────────────────
  NAV: {
    HOME: 'nav.home',
    ABOUT: 'nav.about',
    PHILOSOPHY: 'nav.philosophy',
    BUSINESS: 'nav.business',
    NEWS: 'nav.news',
    CONTACTS: 'nav.contacts',
    GET_QUOTE: 'nav.getQuote',
    GET_A_QUOTE: 'nav.getAQuote',
    OPEN_MENU: 'nav.openMenu',
    CLOSE_MENU: 'nav.closeMenu',
  },

  // ─── Shared / Common ────────────────────────────────────────────────────────
  COMMON: {
    READ_MORE: 'common.readMore',
    CONTACT_US: 'common.contactUs',
    GET_IN_TOUCH: 'common.getInTouch',
    SEND_INQUIRY: 'common.sendInquiry',
    BACK: 'common.back',
    BACK_TO_NEWS: 'common.backToNews',
    BACK_TO_NEWS_LIST: 'common.backToNewsList',
    CLEAR_SEARCH: 'common.clearSearch',
    SEARCH: 'common.search',
    NO_RESULTS: 'common.noResults',
    REQUIRED: 'common.required',
    SUBMIT: 'common.submit',
    SUBMITTING: 'common.submitting',
    SHOWING: 'common.showing',
    OF: 'common.of',
    PRODUCTS: 'common.products',
    ALL: 'common.all',
  },

  // ─── Footer ─────────────────────────────────────────────────────────────────
  FOOTER: {
    TAGLINE: 'footer.tagline',
    HOME: 'footer.home',
    ABOUT_US: 'footer.aboutUs',
    PHILOSOPHY: 'footer.philosophy',
    OUR_BUSINESS: 'footer.ourBusiness',
    NEWS: 'footer.news',
    CONTACTS: 'footer.contacts',
    CHEMICAL_LIST: 'footer.chemicalList',
    EXPORT_TERMS: 'footer.exportTerms',
    PURCHASE_PROCESS: 'footer.purchaseProcess',
    COMPANY_PROFILE: 'footer.companyProfile',
    HISTORY: 'footer.history',
    MISSION_STATEMENT: 'footer.missionStatement',
    CORE_VALUES: 'footer.coreValues',
    TRADING_SERVICES: 'footer.tradingServices',
    IMPORT_EXPORT_TERMS: 'footer.importExportTerms',
    CHEMICAL_TRADE_INFO: 'footer.chemicalTradeInfo',
    PRODUCT_LIST: 'footer.productList',
    LATEST_NEWS: 'footer.latestNews',
    CONTACT_FORM: 'footer.contactForm',
    PURCHASING_PROCESS: 'footer.purchasingProcess',
    COPYRIGHT: 'footer.copyright',
    SITE_POLICY: 'footer.sitePolicy',
    PRIVACY_POLICY: 'footer.privacyPolicy',
    INFO_SECURITY_POLICY: 'footer.infoSecurityPolicy',
  },

  // ─── Home ───────────────────────────────────────────────────────────────────
  HOME: {
    HERO_TITLE: 'home.hero.title',
    NEWS_EYEBROW: 'home.news.eyebrow',
    NEWS_TITLE: 'home.news.title',
    NEWS_READ_MORE: 'home.news.readMore',
    CHEMICALS_EYEBROW: 'home.chemicals.eyebrow',
    CHEMICALS_TITLE: 'home.chemicals.title',
    CHEMICALS_DESC: 'home.chemicals.description',
    CHEMICALS_CTA: 'home.chemicals.cta',
    IMPORT_EXPORT_EYEBROW: 'home.importExport.eyebrow',
    IMPORT_EXPORT_TITLE: 'home.importExport.title',
    IMPORT_EXPORT_DESC: 'home.importExport.description',
    IMPORT_EXPORT_CTA: 'home.importExport.cta',
    PROCESS_EYEBROW: 'home.purchaseProcess.eyebrow',
    PROCESS_TITLE: 'home.purchaseProcess.title',
    PROCESS_DESC: 'home.purchaseProcess.description',
    PROCESS_CTA: 'home.purchaseProcess.cta',
    NEWS_ITEMS: 'home.newsItems',
  },

  // ─── About ──────────────────────────────────────────────────────────────────
  ABOUT: {
    CEO_EYEBROW: 'about.ceo.eyebrow',
    CEO_TITLE: 'about.ceo.title',
    CEO_HEADING: 'about.ceo.heading',
    CEO_P1: 'about.ceo.p1',
    CEO_P2: 'about.ceo.p2',
    CEO_P3: 'about.ceo.p3',
    CEO_CLOSING: 'about.ceo.closing',
    CEO_NAME: 'about.ceo.name',
    PROFILE_EYEBROW: 'about.profile.eyebrow',
    PROFILE_TITLE: 'about.profile.title',
    PROFILE_ITEMS: 'about.profile.items',
    HISTORY_EYEBROW: 'about.history.eyebrow',
    HISTORY_TITLE: 'about.history.title',
    HISTORY_ITEMS: 'about.history.items',
    ACCESS_EYEBROW: 'about.access.eyebrow',
    ACCESS_TITLE: 'about.access.title',
    ACCESS_ADDRESS_TITLE: 'about.access.addressTitle',
    ACCESS_ADDRESS: 'about.access.address',
    ACCESS_PHONE: 'about.access.phone',
    ACCESS_HOURS_TITLE: 'about.access.hoursTitle',
    ACCESS_HOURS: 'about.access.hours',
    ACCESS_STATIONS_TITLE: 'about.access.stationsTitle',
    ACCESS_STATIONS: 'about.access.stations',
    MAP_TITLE: 'about.mapTitle',
  },

  // ─── Mission Statement ──────────────────────────────────────────────────────
  MISSION: {
    EYEBROW: 'mission.eyebrow',
    TITLE: 'mission.title',
    VISION_TITLE: 'mission.vision.title',
    VISION_SUBTITLE: 'mission.vision.subtitle',
    VISION_LABEL: 'mission.vision.label',
    VISION_TEXT: 'mission.vision.text',
    VISION_IMG_ALT: 'mission.vision.imgAlt',
    MISSION_TITLE: 'mission.mission.title',
    MISSION_SUBTITLE: 'mission.mission.subtitle',
    MISSION_LABEL: 'mission.mission.label',
    MISSION_TEXT: 'mission.mission.text',
    MISSION_IMG_ALT: 'mission.mission.imgAlt',
    VALUES_TITLE: 'mission.values.title',
    VALUES_SUBTITLE: 'mission.values.subtitle',
    VALUES_LIST: 'mission.values.list',
    VALUES_IMG_ALT: 'mission.values.imgAlt',
  },

  // ─── Our Business ───────────────────────────────────────────────────────────
  BUSINESS: {
    EYEBROW: 'business.eyebrow',
    HERO_TITLE: 'business.hero.title',
    HERO_DESC: 'business.hero.description',
    SUPPLY_TITLE: 'business.supply.title',
    SUPPLY_DESC: 'business.supply.description',
    SUPPLY_MORE: 'business.supply.moreInfo',
    SUPPLY_CTA: 'business.supply.cta',
    SUPPLY_PRODUCTS: 'business.supply.products',
    MANUFACTURERS_TITLE: 'business.manufacturers.title',
    MANUFACTURERS_SUBTITLE: 'business.manufacturers.subtitle',
    MANUFACTURERS_DESC: 'business.manufacturers.description',
    MANUFACTURERS_CTA: 'business.manufacturers.cta',
    MANUFACTURERS_LIST: 'business.manufacturers.list',
    DISTRIBUTION_TITLE: 'business.distribution.title',
    DISTRIBUTION_DESC: 'business.distribution.description',
    REGIONS: 'business.regions',
  },

  // ─── Product List ───────────────────────────────────────────────────────────
  PRODUCT_LIST: {
    TITLE: 'productList.title',
    DESCRIPTION: 'productList.description',
    SEND_INQUIRY: 'productList.sendInquiry',
    DESCRIPTION_SUFFIX: 'productList.descriptionSuffix',
    SEARCH_PLACEHOLDER: 'productList.searchPlaceholder',
    SEARCH_BTN: 'productList.searchBtn',
    SHOWING: 'productList.showing',
    OF: 'productList.of',
    RESULTS_SUFFIX: 'productList.resultsSuffix',
    COL_NAME: 'productList.col.name',
    COL_FORMULA: 'productList.col.formula',
    COL_HS_CODE: 'productList.col.hsCode',
    COL_APPLICATION: 'productList.col.application',
    COL_CONTACT: 'productList.col.contact',
    ASK_DETAILS: 'productList.askDetails',
    NO_PRODUCTS: 'productList.noProducts',
    NO_PRODUCTS_HINT: 'productList.noProductsHint',
    CLEAR_SEARCH: 'productList.clearSearch',
    FOOTNOTE: 'productList.footnote',
    CTA_TITLE: 'productList.cta.title',
    CTA_DESC: 'productList.cta.description',
    CTA_BTN: 'productList.cta.btn',
  },

  // ─── Contact ─────────────────────────────────────────────────────────────────
  CONTACT: {
    INTRO: 'contact.intro',
    INQUIRY_TYPE_LABEL: 'contact.inquiryType.label',
    INQUIRY_TYPE_PLACEHOLDER: 'contact.inquiryType.placeholder',
    INQUIRY_TYPE_QUOTE: 'contact.inquiryType.quote',
    INQUIRY_TYPE_RECRUITMENT: 'contact.inquiryType.recruitment',
    INQUIRY_TYPE_OTHER: 'contact.inquiryType.other',
    COMPANY_LABEL: 'contact.companyName.label',
    COMPANY_PLACEHOLDER: 'contact.companyName.placeholder',
    NAME_LABEL: 'contact.yourName.label',
    FIRST_NAME: 'contact.yourName.firstName',
    LAST_NAME: 'contact.yourName.lastName',
    PHONE_LABEL: 'contact.phone.label',
    PHONE_PLACEHOLDER: 'contact.phone.placeholder',
    EMAIL_LABEL: 'contact.email.label',
    EMAIL_PLACEHOLDER: 'contact.email.placeholder',
    PRODUCT_LABEL: 'contact.productInterest.label',
    PRODUCT_PLACEHOLDER: 'contact.productInterest.placeholder',
    APPLICATION_LABEL: 'contact.application.label',
    APPLICATION_HINT: 'contact.application.hint',
    DESTINATION_PORT_LABEL: 'contact.destinationPort.label',
    DESTINATION_PORT_PLACEHOLDER: 'contact.destinationPort.placeholder',
    QUANTITY_LABEL: 'contact.quantityPerShipment.label',
    QUANTITY_PLACEHOLDER: 'contact.quantityPerShipment.placeholder',
    DETAILS_LABEL: 'contact.inquiryDetails.label',
    DETAILS_PLACEHOLDER: 'contact.inquiryDetails.placeholder',
    HEAR_ABOUT_LABEL: 'contact.hearAbout.label',
    HEAR_ABOUT_HINT: 'contact.hearAbout.hint',
    HEAR_ABOUT_OPTIONS: 'contact.hearAbout.options',
    PRIVACY_TEXT: 'contact.privacy.text',
    PRIVACY_LINK: 'contact.privacy.link',
    SUCCESS: 'contact.success',
    ERROR: 'contact.error',
    SUBMIT: 'contact.submit',
    SUBMITTING: 'contact.submitting',
  },

  // ─── Purchasing Process ─────────────────────────────────────────────────────
  PURCHASING: {
    TITLE: 'purchasing.title',
    STEPS: 'purchasing.steps',
    CTA_TITLE: 'purchasing.cta.title',
    CTA_DESC: 'purchasing.cta.description',
    CTA_BTN: 'purchasing.cta.btn',
  },

  // ─── Import / Export Terms ──────────────────────────────────────────────────
  IMPORT_EXPORT: {
    TITLE: 'importExport.title',
    SEARCH_PLACEHOLDER: 'importExport.searchPlaceholder',
    FILTER_BY: 'importExport.filterBy',
    ALL: 'importExport.all',
    SHOWING: 'importExport.showing',
    TERMS_SUFFIX: 'importExport.termsSuffix',
    STARTING_WITH: 'importExport.startingWith',
    NO_TERMS_TITLE: 'importExport.noTerms.title',
    NO_TERMS_DESC: 'importExport.noTerms.description',
    ITEMS: 'importExport.items',
  },

  // ─── Chemical Trade Info ────────────────────────────────────────────────────
  TRADE_INFO: {
    NEWS_EYEBROW: 'tradeInfo.news.eyebrow',
    NEWS_TITLE: 'tradeInfo.news.title',
    RESOURCES_EYEBROW: 'tradeInfo.resources.eyebrow',
    RESOURCES_TITLE: 'tradeInfo.resources.title',
    NEWS_ITEMS: 'tradeInfo.newsItems',
    RESOURCE_ITEMS: 'tradeInfo.resourceItems',
    CTA_TITLE: 'tradeInfo.cta.title',
    CTA_DESC: 'tradeInfo.cta.description',
    CTA_BTN: 'tradeInfo.cta.btn',
  },

  // ─── News ───────────────────────────────────────────────────────────────────
  NEWS: {
    LIST_TITLE: 'news.listTitle',
    ITEMS: 'news.items',
  },

  // ─── News Detail ────────────────────────────────────────────────────────────
  NEWS_DETAIL: {
    RECENT_NEWS: 'newsDetail.recentNews',
    BACK_TO_NEWS: 'newsDetail.backToNews',
    BACK_TO_NEWS_LIST: 'newsDetail.backToNewsList',
    NOT_FOUND_TITLE: 'newsDetail.notFound.title',
    NOT_FOUND_DESC: 'newsDetail.notFound.description',
    NOT_FOUND_BACK: 'newsDetail.notFound.back',
  },

  // ─── Not Found ──────────────────────────────────────────────────────────────
  NOT_FOUND: {
    TITLE: 'notFound.title',
    SUGGESTION: 'notFound.suggestion',
  },
} as const;
